import {useEffect, useMemo, useState} from "react";

const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

function getBreakpointValue(breakpoint) {
  if (!(breakpoint in BREAKPOINTS)) throw new Error(`Unknown breakpoint: ${breakpoint}`);
  return BREAKPOINTS[breakpoint];
}

/**
 * Hook returns four functions: breakpointUp(breakpoint), breakpointDown(breakpoint), breakpointIn(lower, upper), breakpointOut(lower, upper).
 * Each returns a boolean based on the current window width.
 * @returns {{breakpointUp: function(*): boolean, breakpointDown: function(*): boolean, breakpointIn: function(*, *): (*|boolean), breakpointOut: function(*, *): *}}
 */
export function useMediaBreakpoint() {
  const getInitialWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 0);
  const [width, setWidth] = useState(getInitialWidth);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function onResize() {
      setWidth(window.innerWidth);
    }

    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { breakpointUp, breakpointDown, breakpointIn, breakpointOut } = useMemo(() => {
    function breakpointUp(breakpoint) {
      const min = getBreakpointValue(breakpoint);
      return width >= min; // inclusive lower bound
    }

    function breakpointDown(breakpoint) {
      const max = getBreakpointValue(breakpoint);
      return width < max; // exclusive upper bound
    }

    function breakpointIn(lower, upper) {
      const min = getBreakpointValue(lower);
      const max = getBreakpointValue(upper);
      if (min > max) throw new Error(`Invalid breakpoint range: ${lower} to ${upper}`);
      return width >= min && width < max; // inclusive lower, exclusive upper
    }

    function breakpointOut(lower, upper) {
      return !breakpointIn(lower, upper);
    }

    return { breakpointUp, breakpointDown, breakpointIn, breakpointOut };
  }, [width]);

  return { breakpointUp, breakpointDown, breakpointIn, breakpointOut };
}
